import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { Container, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HighlightsLightSection } from '@/types/sections/HighlightsSection';
import { Icon } from '@/components/Icon';
import { getIconName } from '@/components/Icon/Icon';

type HighlightsLightProps = BoxProps & {
  section: HighlightsLightSection;
};

export default function HighlightsLight({ sx, section, ...other }: HighlightsLightProps) {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 2, md: 10 } }}>
        {section?.small && (
          <m.div variants={varFade('inDown')}>
            <Typography variant="overline" sx={{ color: 'text.disabled', textAlign: 'center' }}>
              {section?.small}
            </Typography>
          </m.div>
        )}

        {section?.title && (
          <m.div variants={varFade('inUp')}>
            <Typography variant="h3" sx={{ my: 3, color: 'primary.main', textAlign: 'center' }}>
              {section?.title}
            </Typography>
          </m.div>
        )}

        {section?.subtitle && (
          <m.div variants={varFade('inUp')}>
            <Typography
              sx={{ mx: 'auto', my: 3, maxWidth: 992, color: 'text.secondary', textAlign: 'center' }}>
              {section?.subtitle}
            </Typography>
          </m.div>
        )}
        <Box
          sx={{
            gap: 5,
            mt: 5,
            mb: 16,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          }}>
          {section?.highlights &&
            section.highlights.map((highlight) => (
              <Card key={highlight.title} sx={{ borderRadius: 0, boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.25)' }}>
                <CardContent sx={{ px: 5, py: 4, textAlign: 'left' }}>
                  {highlight.icon && (
                    <Icon
                      name={getIconName(highlight.icon, 'unknown')}
                      color={theme.palette.primary.main}
                      size="40"
                    />
                  )}

                  <Typography variant="body1" color="primary" sx={{ mb: 1, mt: 2 }}>
                    {highlight.title}
                  </Typography>

                  {highlight.subtitle && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {highlight.subtitle}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
        </Box>
      </Container>
    </Box>
  );
}
